import {beginMutation} from '../../../pwa-update.mjs';
import { apiBoundary } from '../../../api-boundary.mjs';
import { CanceledError } from 'axios';
import { prepareIdempotency, settleIdempotency, clearIdempotencyMemory } from '../../../idempotency.mjs';
import { getSessionGeneration, getSessionSignal, onSessionReset } from '../../../session-runtime.ts';
import pkg from '../../../../../package.json' assert { type: 'json' };
import { CookieComponent } from '../../../../_mesomb/assets/ts/components';
import { logout } from '../../../store/auth.ts';
import { getEnvironment } from '../../../environment.ts';
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import type { AppStore } from '../../../store/index.ts';
import type { AuthSliceState } from '../../../store/auth.ts';
import type { ApplicationSliceState } from '../../../store/application.ts';
import {getApplicationGeneration, getApplicationSignal, reportApplicationAccessFailure} from '../../../application-runtime';
import i18n from 'i18next';
import {setTerms} from '../../../store/terms';
import type {TermAndConditionModel} from '../../../../types';

interface IdempotencyConfig extends InternalAxiosRequestConfig {
  mesombIdempotencyFingerprint?: string;
  mesombSessionGeneration?: number;
  mesombApplicationGeneration?: number;
  mesombApplicationKey?: string;
  mesombDetachSignal?: () => void;
  mesombFinishMutation?: ReturnType<typeof beginMutation>;
}

onSessionReset(clearPending => { if (clearPending) clearIdempotencyMemory(); });

export function setupAxios(axios: AxiosInstance, store?: AppStore) {
  const readState = () => store?.getState() as unknown as {
    auth: AuthSliceState; application: ApplicationSliceState;
  } | undefined;
  axios.defaults.headers.Accept = 'application/json';
  axios.defaults.baseURL = `${import.meta.env.VITE_APP_BACKEND_HOST}/api/v1.1/`;
  axios.defaults.withCredentials = false;
  axios.defaults.withXSRFToken = false;
  axios.defaults.timeout = 60000;
  axios.interceptors.request.use(
    async (config: IdempotencyConfig) => {
      // Validate before reading cookies, merchant state or generating operation keys.
      const destination = apiBoundary(axios, config, import.meta.env.VITE_APP_BACKEND_HOST);
      config.withXSRFToken = false;
      config.xsrfCookieName = 'csrftoken';
      config.xsrfHeaderName = 'X-CSRFToken';
      config.headers.set('X-MeSomb-Source', `Web/${pkg.version}`);
      // API-owned labels must use the language currently displayed, not a stale profile preference.
      const locale = i18n.resolvedLanguage || i18n.language || readState()?.auth.user?.locale;
      if (locale) config.headers.set('Accept-Language', locale);
      config.headers.delete('X-MeSomb-Application');
      config.mesombSessionGeneration = getSessionGeneration();
      const sessionSignal = getSessionSignal();
      const applicationSignal = destination.applicationBound && !config.mesombNavigation ? getApplicationSignal() : undefined;
      if (applicationSignal) config.mesombApplicationGeneration = getApplicationGeneration();
      const originalSignal = config.signal;
      const controller = new AbortController();
      const abort = () => controller.abort();
      sessionSignal.addEventListener('abort', abort, {once:true});
      applicationSignal?.addEventListener('abort', abort, {once:true});
      originalSignal?.addEventListener?.('abort', abort, {once:true});
      if (sessionSignal.aborted || originalSignal?.aborted || applicationSignal?.aborted) abort();
      config.signal = controller.signal;
      config.mesombDetachSignal = () => {
        sessionSignal.removeEventListener('abort', abort);
        applicationSignal?.removeEventListener('abort', abort);
        originalSignal?.removeEventListener?.('abort', abort);
      };
      config.withCredentials = true;
      config.headers['X-MeSomb-Environment'] = getEnvironment();
      config.headers['X-CSRFToken'] = CookieComponent.get('csrftoken');
      const applicationKey = config.params?.filter_application ?? readState()?.application.application?.key;
      if (destination.applicationBound) config.mesombApplicationKey = applicationKey;
      if (applicationKey && destination.applicationScoped) {
        config.params = { ...config.params, filter_application: applicationKey };
        config.headers['X-MeSomb-Application'] = applicationKey;
      }
      try {
        if (['post','put','patch','delete'].includes(config.method?.toLowerCase() || '')) config.mesombFinishMutation = beginMutation();
        await prepareIdempotency(config, {
          environment:getEnvironment(), application:config.headers.get('X-MeSomb-Application'),
          user:readState()?.auth.user?.id ?? 'public',
        });
      } catch (error) { config.mesombDetachSignal?.(); config.mesombFinishMutation?.({status:400} as import('axios').AxiosResponse); throw error; }
      if (config.mesombSessionGeneration !== getSessionGeneration() || controller.signal.aborted ||
        (config.mesombApplicationGeneration !== undefined && config.mesombApplicationGeneration !== getApplicationGeneration())) {
        config.mesombDetachSignal?.();
        config.mesombFinishMutation?.({status:400} as import('axios').AxiosResponse);
        throw new CanceledError('Session changed');
      }
      config.url = destination.url;
      config.baseURL = undefined;
      return config;
    },
    (err: unknown) => Promise.reject(err)
  );
  axios.interceptors.response.use(
    async function (response) {
      const config = response.config as IdempotencyConfig;
      config.mesombFinishMutation?.(response, String(config.headers.get('Idempotency-Key') || ''));
      config.mesombDetachSignal?.();
      if (config.mesombSessionGeneration !== getSessionGeneration() ||
        (config.mesombApplicationGeneration !== undefined && config.mesombApplicationGeneration !== getApplicationGeneration())) {
        throw new CanceledError('Session changed');
      }
      await settleIdempotency(config, response);
      return response;
    },
    async function (error: AxiosError<{ code?: string; error?: { code?: string }; meta?: { is_authenticated?: boolean } }>) {
      const config = error.config as IdempotencyConfig | undefined;
      config?.mesombFinishMutation?.(error.response, String(config.headers.get('Idempotency-Key') || ''));
      config?.mesombDetachSignal?.();
      if ((config?.mesombSessionGeneration !== undefined && config.mesombSessionGeneration !== getSessionGeneration()) ||
        (config?.mesombApplicationGeneration !== undefined && config.mesombApplicationGeneration !== getApplicationGeneration())) {
        return Promise.reject(new CanceledError('Session changed'));
      }
      const code = error.response?.data?.error?.code;
      if (error.response?.status === 403 && error.response.data?.code === 'terms_acceptance_required') {
        // Never replay the blocked mutation. Let the user review the current document.
        try {
          const {data} = await axios.get<TermAndConditionModel | null>('terms/');
          if (data && config?.mesombSessionGeneration === getSessionGeneration()) store?.dispatch(setTerms(data));
        } catch { /* Keep the original denied operation as the reported error. */ }
      }
      // Revalidate membership after denied reads, never replay a financial mutation.
      // A missing detail record is not evidence that the application was deleted.
      if (config?.mesombApplicationKey && ['get', 'head'].includes(config.method ?? 'get') &&
        (error.response?.status === 403 || (error.response?.status === 404 &&
          /\/business\/applications\/[^/]+\/?(?:\?|$)/.test(config.url ?? '')))) {
        reportApplicationAccessFailure(config.mesombApplicationKey);
      }
      // django-allauth uses 401 for both an expired session and a required
      // step-up authentication. The latter keeps the session authenticated and
      // provides the allowed reauthentication flows in the response body.
      const reauthenticationRequired = error.response?.status === 401 &&
        error.response.data?.meta?.is_authenticated === true;
      await settleIdempotency(config, error.response);
      if (
        (!reauthenticationRequired && (error.response?.status === 401 || error.response?.data?.code === 'not_authenticated' || code === 'not_authenticated'))
      ) {
        store?.dispatch(logout());
        if (!location.pathname.startsWith('/auth')) window.location.href = '/auth';
      }
      return Promise.reject(error);
    }
  );
}

export function setAuthUrl(endpoint: string) {
  return `${import.meta.env.VITE_APP_BACKEND_HOST}/api/auth/browser/v1/${endpoint}`;
}

// export {getAuth, saveAuth, removeAuth, AUTH_LOCAL_STORAGE_KEY, USER_LOCAL_STORAGE_KEY, saveUser, getUser, removeUser}
